import { Box, HStack, Tooltip } from '@chakra-ui/react';

export interface SettingsSegment<T extends string> {
    value: T;
    label: string;
    /** Explanation for the option, shown on hover over the segment. */
    tooltip?: string;
    isDisabled?: boolean;
}

export interface SettingsSegmentedControlProps<T extends string> {
    segments: ReadonlyArray<SettingsSegment<T>>;
    value: T;
    onChange: (value: T) => void;
    size?: 'sm' | 'md';
}

/**
 * A pill-shaped picker for a small set of mutually exclusive options, used in
 * place of a radio group where the choice reads as a mode ("All / Partial /
 * Off") rather than a list. The track is a tinted trough; the selected segment
 * is a raised white pill.
 */
const SettingsSegmentedControl = <T extends string>({
    segments,
    value,
    onChange,
    size = 'sm',
}: SettingsSegmentedControlProps<T>) => {
    const isSmall = size === 'sm';
    return (
        <HStack
            spacing={0}
            bg="bg.subtle"
            borderRadius="lg"
            p="2px"
            flexShrink={0}
            display="inline-flex"
        >
            {segments.map((segment) => {
                const isActive = segment.value === value;
                const isDisabled = segment.isDisabled === true;
                const button = (
                    <Box
                        as="button"
                        type="button"
                        key={segment.value}
                        onClick={() => !isDisabled && onChange(segment.value)}
                        disabled={isDisabled}
                        cursor={isDisabled ? 'not-allowed' : 'pointer'}
                        px={isSmall ? 3 : 4}
                        py={isSmall ? 1 : 1.5}
                        borderRadius="md"
                        borderWidth="1px"
                        // The border is always present so selecting a segment
                        // never shifts the track's width by a pixel.
                        borderColor={isActive ? 'gray.200' : 'transparent'}
                        bg={isActive ? 'white' : 'transparent'}
                        boxShadow={isActive ? '0 1px 2px rgba(16, 24, 40, 0.06)' : 'none'}
                        color={isDisabled ? 'gray.400' : isActive ? 'gray.800' : 'gray.500'}
                        fontSize={isSmall ? 'sm' : 'md'}
                        fontWeight={isActive ? 'semibold' : 'medium'}
                        lineHeight={1.4}
                        whiteSpace="nowrap"
                        transition="background 0.15s ease, color 0.15s ease, border-color 0.15s ease"
                        _hover={{
                            color: isDisabled || isActive ? undefined : 'gray.700',
                        }}
                    >
                        {segment.label}
                    </Box>
                );
                return segment.tooltip ? (
                    <Tooltip
                        key={segment.value}
                        label={segment.tooltip}
                        placement="top"
                        hasArrow
                        fontSize="sm"
                    >
                        {button}
                    </Tooltip>
                ) : (
                    button
                );
            })}
        </HStack>
    );
};

export default SettingsSegmentedControl;
